import { LoadingOrbit, Text } from '@chatsdet/ui';
import { CheckCircle } from '@phosphor-icons/react';
import type { StepVerdict } from '../../../features/external-tests/scenarios';
import type { ExternalRunDetailDto } from '../../../features/external-tests/useExternalRun';

type Props = {
  provider: string;
  runStatus: ExternalRunDetailDto['status'];
  verdict: StepVerdict;
  recordingStatus: NonNullable<ExternalRunDetailDto['recordings']>['status'];
};

const isActiveRun = (status: ExternalRunDetailDto['status']) =>
  status === 'open' || status === 'collecting';

/** Both supported browser runners emit capture lifecycle updates. */
const supportsCapture = (provider: string) => provider === 'selenium' || provider === 'playwright';

/** Full-height recording state shown while the evidence pane has no video. */
export function ExternalRecordingPlaceholder({
  provider,
  runStatus,
  verdict,
  recordingStatus,
}: Props) {
  const passedWithoutRecording = verdict === 'passed';
  const recordingPending =
    supportsCapture(provider) && (isActiveRun(runStatus) || recordingStatus === 'preparing');
  const recordingFailed = supportsCapture(provider) && recordingStatus === 'partial';

  return (
    <div
      className="flex min-h-[240px] flex-1 items-center justify-center px-6 py-10 text-center"
      data-testid="external-recording-placeholder"
    >
      <div className="flex max-w-md flex-col items-center gap-3">
        {recordingPending ? (
          <LoadingOrbit size="sm" />
        ) : passedWithoutRecording ? (
          <CheckCircle size={34} weight="fill" className="text-success" aria-hidden="true" />
        ) : null}
        <Text size="sm" weight="medium" className="text-foreground">
          {recordingPending
            ? 'Preparing test recording'
            : passedWithoutRecording
              ? 'Test passed'
              : 'Recording unavailable'}
        </Text>
        <Text size="sm" className="text-pretty text-muted-foreground">
          {recordingPending
            ? 'The run is complete enough to review while its per-test video is prepared.'
            : recordingFailed
              ? 'The per-test recording could not be prepared. The test result is unaffected.'
              : passedWithoutRecording
                ? 'This run was not configured to record passing tests.'
                : provider === 'cypress'
                  ? 'Cypress records video only — console and network are not available for this provider.'
                  : provider === 'playwright'
                    ? 'No recording was captured for this test. Playwright records a test only when it is retried or the suite records every attempt.'
                    : 'No recording was captured for this test.'}
        </Text>
      </div>
    </div>
  );
}
